/**
 * Debug API client.
 */

import { Result } from "true-myth/result";

import type { AuthStrategy } from "../../config/api-config";
import type {
  DebugError,
  DebugStatusResponse,
  FireLogResult,
  SlowResponseResult,
} from "./types";

function createAuthFetch(auth: AuthStrategy) {
  return (url: string, options: RequestInit = {}) => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (auth.type === "bearer") {
      const token = auth.getToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    return fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...(options.headers as Record<string, string>),
      },
      credentials: auth.type === "cookie" ? "include" : "omit",
    });
  };
}

export function createDebugApi(baseUrl: string, auth: AuthStrategy) {
  const authFetch = createAuthFetch(auth);
  async function getDebugStatus(): Promise<
    Result<DebugStatusResponse, DebugError>
  > {
    const res = await authFetch(`${baseUrl}/debug/status`);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return Result.err({
        message: data.message ?? `Failed to get debug status: ${res.status}`,
      });
    }
    const data = (await res.json()) as DebugStatusResponse;
    return Result.ok(data);
  }

  async function triggerTestError(): Promise<Result<void, DebugError>> {
    const res = await authFetch(`${baseUrl}/debug/throw`);
    // This endpoint always returns 500 on success (it throws an error)
    // We consider it "successful" if we got a response at all
    if (res.status === 500) {
      return Result.ok(undefined);
    }
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return Result.err({
        message: data.message ?? `Failed to trigger error: ${res.status}`,
      });
    }
    return Result.ok(undefined);
  }

  async function triggerSlowResponse(): Promise<
    Result<SlowResponseResult, DebugError>
  > {
    const res = await authFetch(`${baseUrl}/debug/slow`);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return Result.err({
        message:
          data.message ?? `Failed to trigger slow response: ${res.status}`,
      });
    }
    const data = (await res.json()) as SlowResponseResult;
    return Result.ok(data);
  }

  async function triggerTestLog(): Promise<Result<FireLogResult, DebugError>> {
    const res = await authFetch(`${baseUrl}/debug/log`, { method: "POST" });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return Result.err({
        message: data.message ?? `Failed to fire test log: ${res.status}`,
      });
    }
    const data = (await res.json()) as FireLogResult;
    return Result.ok(data);
  }

  return {
    getDebugStatus,
    triggerTestError,
    triggerSlowResponse,
    triggerTestLog,
  };
}

export type DebugApi = ReturnType<typeof createDebugApi>;
